import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import {
  setWsConnection,
  setConnected,
  addMessage,
  setUserTyping,
  endCall,
  setUserConnected,
  setUserDisconnected,
  updateChatData,
} from "../slices/chat.slice";
import { setShowUserJoinedModal } from "../slices/auth.slice";
import {
  setIncomingCall,
  applyMvoiceCallEnded,
  attachCallRecordNumber,
  hydrateMvoiceActiveCallsSnapshot,
} from "../slices/callCenter.slice";
import {
  ChatDataPayload,
  IChatMessage,
  IUserChatData,
  RootState,
} from "../types";
import {
  END_CALL_CODE,
  noDataLabel,
  TYPING_CODE,
  VIDEO_OFF_CODE,
  VIDEO_ON_CODE,
} from "../../utils/constants";
import { BASE_URL_DYNAMIC } from "./base-query";
import { mapMvoiceRecordNumberPayload } from "../mvoice/mapMvoicePayloads";
import { MVOICE_RECORD_NUMBER_EVENT } from "../mvoice/mvoice.constants";
import type { TIncomingCall } from "../../types/callCenter.types";
import { paycallApi } from "./paycall.api";
import { handlePaycallCallTaken } from "../paycall/paycall.handlers";
import {
  mapPaycallEndPayload,
  mapPaycallStartPayload,
} from "../paycall/mapPaycallPayloads";

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  endpoints: (builder) => ({
    initializeConnection: builder.query<void, string | number>({
      // @ts-ignore
      async queryFn(userId, { dispatch, getState }) {
        const state = getState() as RootState;
        const isAuth = state.user.isAuth;

        if (!isAuth) return { error: "User is not logged in" };

        const apiBase = BASE_URL_DYNAMIC.replace(/\/?$/, "");

        const connection = new HubConnectionBuilder()
          .withUrl(`${apiBase}/chathub`, {
            // Read token fresh on every request — handles token refresh correctly
            accessTokenFactory: () => {
              const currentState = getState() as RootState;
              return currentState.user.accessToken ?? "";
            },
            withCredentials: false,
          })
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .build();

        connection.onreconnecting((error) => {
          console.warn(`Connection lost due to error "${error}". Reconnecting...`);
          dispatch(setConnected(false));
        });

        connection.onreconnected(() => {
          dispatch(setConnected(true));
        });

        connection.onclose((error) => {
          // console.error("Connection closed permanently:", error);
          dispatch(setConnected(false));
        });

        connection.on("ReceiveChatData", (data:  IUserChatData) => {
          dispatch(updateChatData(data));
        });

        connection.on("ReceiveChatDataUsers", (data: ChatDataPayload) => {
          if (Array.isArray(data)) {
            dispatch(updateChatData(data));
          }
        });

        connection.on("onMessage", (message: IChatMessage) => {
          if (message.content.includes(TYPING_CODE)) {
            dispatch(setUserTyping(message));
          } else if (message.content.includes(END_CALL_CODE)) {
            const nameFromContent = message.content.split("by:")[1];
            let by = nameFromContent;
            if (!by) {
              const currentState = getState() as RootState;
              const conversation = currentState.chat.chatData.conversations.find(
                (c) => c.userId === message.senderId
              );
              by = conversation?.fullName || noDataLabel;
            }
            dispatch(endCall(by));
          } else if (
            message.content.includes(VIDEO_OFF_CODE) ||
            message.content.includes(VIDEO_ON_CODE)
          ) {
            return;
          } else {
            dispatch(addMessage(message));
          }
        });

        connection.on("UserConnected", (data: number) => {
          dispatch(setUserConnected(data));
          data !== +userId && dispatch(setShowUserJoinedModal(data));
        });

        connection.on("UserDisconnected", (disconnectedUserId: number) => {
          dispatch(setUserDisconnected(disconnectedUserId));
        });

        connection.on("ReceiveError", (error: string) => {
          console.error("SignalR error:", error);
        });

        connection.on("PaycallCallStart", (payload: unknown) => {
          const mapped = mapPaycallStartPayload(payload);
          if (mapped) {
            dispatch(setIncomingCall(mapped));
          }
        });

        connection.on("PaycallCallTaken", (payload: unknown) => {
          handlePaycallCallTaken(dispatch, payload);
        });

        connection.on("PaycallCallEnd", (payload: unknown) => {
          const cdr = mapPaycallEndPayload(payload);
          if (cdr) {
            dispatch(applyMvoiceCallEnded(cdr));
          }
        });

        connection.on(MVOICE_RECORD_NUMBER_EVENT, (payload: unknown) => {
          const mapped = mapMvoiceRecordNumberPayload(payload);
          if (mapped) {
            dispatch(attachCallRecordNumber(mapped));
          }
        });

        /**
         * Hydrate active ringing calls before the hub connects (page refresh).
         */
        try {
          const snapshotPromise = dispatch(
            paycallApi.endpoints.getPaycallActiveCalls.initiate(undefined, {
              subscribe: false,
              forceRefetch: true,
            })
          );
          const snap = await snapshotPromise.unwrap();
          const raw = snap?.activeCalls;
          if (Array.isArray(raw)) {
            const mapped = raw
              .map((p) => mapPaycallStartPayload(p))
              .filter((x): x is TIncomingCall => x != null);
            if (mapped.length > 0) {
              dispatch(hydrateMvoiceActiveCallsSnapshot(mapped));
            }
          }
        } catch {
          /* Snapshot is optional; live SignalR remains authoritative */
        }

        if (connection.state !== HubConnectionState.Disconnected) {
          console.warn("Connection is already started or not in Disconnected state.");
          return { error: "Connection is not in Disconnected state." };
        }

        try {
          await connection.start();
          dispatch(setWsConnection(connection));
          dispatch(setConnected(true));
          return { data: null };
        } catch (error: any) {
          console.error("Connection error during start:", error);
          return { error: error.message ?? "Failed to connect" };
        }
      },
    }),

    sendMessage: builder.mutation<void, IChatMessage>({
      async queryFn(message, { getState }) {
        const state = getState() as RootState;
        const connection = state.chat.connection;

        if (!connection) {
          return { error: "WebSocket connection not established" };
        }

        if (connection.state !== HubConnectionState.Connected) {
          return { error: "Connection is not in Connected state." };
        }

        try {
          await connection.invoke("SendMessage", message);
          return { data: undefined };
        } catch (error: any) {
          console.error("SendMessage error:", error);
          return { error: error.message ?? "Failed to send message" };
        }
      },
    }),
  }),
});

export const { useLazyInitializeConnectionQuery } = chatApi;